import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';

// Friendly copy per limited feature. `limit`/`current` come from the API's
// 402 payload so the numbers always match the server's enforcement.
const FEATURE_COPY = {
    trialEnded: {
        title: 'Your trial has ended',
        body: () =>
            "Subscribe to keep searching. Everything you've saved is still here: your saved traders, notes, calls and reminders. Need a little longer to try it? Get in touch.",
    },
    searchCredits: {
        title: "You've used your free searches",
        body: () =>
            'Subscribe for unlimited searches, every company in every search, New to trade and downloads. Need a little longer to try it? Get in touch and we can add more.',
    },
    searches: {
        title: 'Monthly search limit reached',
        body: (limit) =>
            `Your plan includes ${limit} searches per month. Your quota resets on the 1st — or subscribe for unlimited searches.`,
    },
    savedTraders: {
        title: 'Saved trader limit reached',
        body: (limit) =>
            `Your plan allows up to ${limit} saved traders. Remove some to free up space, or subscribe for unlimited saved traders.`,
    },
    savedSearches: {
        title: 'Saved search limit reached',
        body: (limit) =>
            `Your plan allows ${limit} saved search${limit === 1 ? '' : 'es'}. Subscribe for up to 10.`,
    },
    seats: {
        title: 'Your plan includes one seat',
        body: () =>
            'Team members come with a subscription. Subscribe to add as many people as you need.',
    },
    newToTrade: {
        title: 'New to trade comes with a subscription',
        body: () =>
            'See every company that has just started importing or exporting near you, each month. Included when you subscribe.',
    },
    mapAreaSearch: {
        title: 'Load more as you move the map',
        body: () =>
            'With a subscription the map fills in as you pan and zoom. On free searches you can explore the results you searched for.',
    },
    exportSavedTraders: {
        title: 'Downloading comes with a subscription',
        body: () =>
            'Download your saved traders as a spreadsheet to use in your CRM or share with the team. Subscribe to unlock it.',
    },
    radiusSearch: {
        title: 'Radius search comes with a subscription',
        body: () =>
            'Searching by postcode radius comes with a subscription. Subscribe to find traders within a set distance of any postcode.',
    },
};

// Listens app-wide for plan-limit rejections (dispatched by the API client on
// HTTP 402 / SSE limit errors) and shows an upgrade prompt. Mounted once in Layout.
const UpgradeModal = () => {
    const navigate = useNavigate();
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        const onLimit = (e) => setDetail(e.detail || {});
        window.addEventListener('fs:limit', onLimit);
        return () => window.removeEventListener('fs:limit', onLimit);
    }, []);

    if (!detail) return null;

    const copy = FEATURE_COPY[detail.feature] || {
        title: 'Plan limit reached',
        body: () => detail.error || 'You have hit a limit on your current plan.',
    };

    return (
        <Modal
            open
            onClose={() => setDetail(null)}
            kicker="Upgrade"
            title={copy.title}
            footer={
                <>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setDetail(null)}
                    >
                        Not now
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                            setDetail(null);
                            navigate('/subscription');
                        }}
                    >
                        See plans &amp; usage <span className="arrow">→</span>
                    </button>
                </>
            }
        >
            <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-2)' }}>
                {copy.body(detail.limit)}
            </p>
            {typeof detail.current === 'number' && typeof detail.limit === 'number' && (
                <p
                    style={{
                        margin: '12px 0 0',
                        fontFamily: 'var(--font-mono)',
                        fontSize: 11,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: 'var(--muted)',
                    }}
                >
                    {detail.current} / {detail.limit} used
                </p>
            )}
        </Modal>
    );
};

export default UpgradeModal;
